/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { assign, createMachine } from 'xstate';
import ExtJS from '../../../../../../../interface/ExtJS';
import { ContainersNotAvailableError, fetchContainers } from './containerQuarantine.api';
import { CONTAINER_STRINGS as STRINGS } from './containerQuarantine.strings';
import {
  DEFAULT_CONTAINER_QUERY,
  type ContainerPage,
  type ContainerQuery,
  type ContainerSortField,
} from './containerQuarantine.types';

export interface ContainerListContext {
  query: ContainerQuery;
  data: ContainerPage | null;
  error: string | null;
}

export type ContainerListEvent =
  | { type: 'SET_SORT'; sortBy: ContainerSortField; asc: boolean }
  | { type: 'SET_PAGE'; page: number }
  | { type: 'SET_PAGE_SIZE'; pageSize: number }
  | { type: 'RETRY' };

type EventOf<T extends ContainerListEvent['type']> = Extract<ContainerListEvent, { type: T }>;
type FetchError = { type: 'error.platform.fetchContainers'; data: unknown };

const withQuery = (context: ContainerListContext, patch: Partial<ContainerQuery>): ContainerQuery => ({
  ...context.query,
  ...patch,
  page: patch.page ?? 1,
});

const setSort = assign<ContainerListContext, EventOf<'SET_SORT'>>({
  query: (context, event) => withQuery(context, { sortBy: event.sortBy, asc: event.asc }),
});
const setPage = assign<ContainerListContext, EventOf<'SET_PAGE'>>({
  query: (context, event) => withQuery(context, { page: event.page }),
});
const setPageSize = assign<ContainerListContext, EventOf<'SET_PAGE_SIZE'>>({
  query: (context, event) => withQuery(context, { pageSize: event.pageSize }),
});

const setLoaded = assign<ContainerListContext, { type: 'done.invoke.fetchContainers'; data: ContainerPage }>({
  data: (_context, event) => event.data,
  error: () => null,
});
const setFailed = assign<ContainerListContext, FetchError>({
  data: () => null,
  error: (_context, event) => (event.data instanceof Error ? event.data.message : STRINGS.errors.loadList),
});

// Handled in every state but `unavailable`: re-entering `loading` cancels the in-flight request.
const queryChanges = {
  SET_SORT: { target: 'loading', actions: 'setSort' },
  SET_PAGE: { target: 'loading', actions: 'setPage', cond: 'pageChanged' },
  SET_PAGE_SIZE: { target: 'loading', actions: 'setPageSize' },
};

export const containerListMachine = createMachine<ContainerListContext, ContainerListEvent>(
  {
    id: 'containerList',
    predictableActionArguments: true,
    initial: 'loading',
    context: { query: DEFAULT_CONTAINER_QUERY, data: null, error: null },
    states: {
      loading: {
        invoke: {
          id: 'fetchContainers',
          src: 'fetchContainers',
          onDone: { target: 'loaded', actions: 'setLoaded' },
          onError: [
            { target: 'unavailable', cond: 'notAvailable' },
            { target: 'failed', actions: 'setFailed' },
          ],
        },
        on: queryChanges,
      },
      loaded: { on: queryChanges },
      failed: { on: { ...queryChanges, RETRY: 'loading' } },
      // IQ cannot list this instance's containers yet; nothing the user does here changes that.
      unavailable: { type: 'final' },
    },
  },
  {
    services: {
      fetchContainers: async (context) => {
        await ExtJS.waitForPermissions();
        return fetchContainers(context.query);
      },
    },
    guards: {
      notAvailable: (_context, event) => (event as FetchError).data instanceof ContainersNotAvailableError,
      pageChanged: (context, event) => event.type === 'SET_PAGE' && event.page !== context.query.page,
    },
    actions: { setSort, setPage, setPageSize, setLoaded, setFailed },
  }
);
